import { Fragment } from 'react'

import {
  IconStarHalf,
  IconStarSolid,
  Space,
  SubText,
  renderIcon,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

const STAR_GLYPH_SIZE = 14
const HALF_STAR_THRESHOLD = 0.25
const FULL_STAR_THRESHOLD = 0.75
const UNEARNED_STAR_COLOR = 'lightGrey'

export interface ICatalogItemRatingProps {
  value: number
  count: number
  scaleMax: number
}

export const CatalogItemRating = ({
  value,
  count,
  scaleMax,
}: ICatalogItemRatingProps) => {
  const intl = useProcurifyIntl()

  const stars = Array.from({ length: scaleMax }, (_, index) => {
    const remainder = value - index
    const position = index + 1
    if (remainder >= FULL_STAR_THRESHOLD) {
      return { position, glyph: <IconStarSolid />, isEarned: true }
    }
    if (remainder >= HALF_STAR_THRESHOLD) {
      return { position, glyph: <IconStarHalf />, isEarned: true }
    }
    return { position, glyph: <IconStarSolid />, isEarned: false }
  })

  const ariaLabel = intl.formatMessage(messages.ratingAriaLabel, {
    value,
    scaleMax,
    count,
  })

  return (
    <span role='img' aria-label={ariaLabel}>
      <Space direction='horizontal' size='xsmall' align='center'>
        <SubText inline bold>
          {intl.formatNumber(value, { maximumFractionDigits: 1 })}
        </SubText>
        <span aria-hidden>
          <Space direction='horizontal' size='xsmall' align='center'>
            {stars.map(({ position, glyph, isEarned }) => (
              <Fragment key={`star-${position}`}>
                {renderIcon(glyph, {
                  size: STAR_GLYPH_SIZE,
                  ...(!isEarned && { color: UNEARNED_STAR_COLOR }),
                })}
              </Fragment>
            ))}
          </Space>
        </span>
        <SubText inline color='textSecondary'>
          {intl.formatMessage(messages.reviewCount, { count })}
        </SubText>
      </Space>
    </span>
  )
}
